import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { useMutationTruckShift } from 'src/hooks/useMutationTruckShift';

/** 勤務の解決に必要な最小の delivery 情報。id は shift_truck.id */
type ShiftBearingDelivery = Pick<PlanningsDeliveryEntity, 'id' | 'truckId'> & { shiftId?: number | null };

export type UseShiftDeletionDeps = {
  deliveries: ShiftBearingDelivery[];
  /** 削除後の再取得。画面ごとに対象クエリが違うので注入する */
  refetchAfterDelete: () => Promise<void>;
  notify: (message: string, variant: 'success' | 'error') => void;
  /** 確認を挟む。破壊的操作なので必須 */
  confirm: (params: { title: string; description: string; onConfirm: () => Promise<void> }) => void;
  /** 押せない理由（自動配車の実行中など）。返り値があれば実行しない */
  getBlockedReason?: () => string | null;
};

/**
 * 「計画無」バッジの × 。勤務（Shift）ごと削除する。
 *
 * `DELETE /api/v5/truck_shifts/:id` は稼働表と同じエンドポイントで、Shift を消すと
 * shift_trucks / deliveries に dependent: :destroy で波及する。
 * 「乗り換え（複数トラック）の勤務」「確定済の配車がある勤務」は API 側が 422 で弾くので、
 * 返ってきた文言をそのまま出す（FE で条件を二重に持つと必ずずれる）。
 */
export function useShiftDeletion({
  deliveries, refetchAfterDelete, notify, confirm, getBlockedReason,
}: UseShiftDeletionDeps) {
  const { remove } = useMutationTruckShift();
  /**
   * 削除中の勤務。押した時点で画面から消すために持つ。
   *
   * 削除は API の応答まで 1〜数秒かかり、その間カードが残ると「押しても消えない」
   * ように見える。失敗したらここから外して元に戻す。
   */
  const [deletingShiftTruckIds, setDeletingShiftTruckIds] = useState<Set<number>>(new Set());

  // サーバの一覧から消えたら追跡をやめる。成功時にすぐ外すと、refetch が返る前の
  // 古い一覧で一瞬復活して見えるので、消えたことを確認してから外す
  useEffect(() => {
    setDeletingShiftTruckIds((prev) => {
      if (prev.size === 0) return prev;
      const alive = new Set(deliveries.map((d) => d.id));
      const next = new Set([...prev].filter((id) => alive.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [deliveries]);
  // useMutation の戻り値は毎レンダー新しいオブジェクト。mutateAsync は安定なので、
  // 依存にはこちらを使う（オブジェクトを依存にすると memo 済みの呼び出し側が毎回作り直される）
  const removeMutateAsync = remove.mutateAsync;

  /**
   * shiftTruckId は押したカードの勤務。1 台に複数のドライバーが同じ時間帯で乗る勤務では、
   * truckId から引くとどの行の × を押しても最初の 1 人の勤務を消してしまう。
   * 固定ルート由来の合成行しか無いグループには勤務が無いので null が来る。
   */
  const requestDeleteShift = useCallback((shiftTruckId: number | null) => {
    const target = shiftTruckId != null ? deliveries.find((d) => d.id === shiftTruckId) : undefined;
    const shiftId = target?.shiftId;
    if (shiftId == null) return;

    confirm({
      title: 'この勤務を削除しますか？',
      description: '勤務と配車枠を削除します。この操作は元に戻せません。',
      onConfirm: async () => {
        const blocked = getBlockedReason?.();
        if (blocked) { notify(blocked, 'error'); return; }

        // 押した時点で画面から消す。失敗したら下の catch で戻す
        const hiddenId = target?.id;
        if (hiddenId != null) {
          setDeletingShiftTruckIds((prev) => new Set(prev).add(hiddenId));
        }
        try {
          await removeMutateAsync(shiftId);
        } catch (e) {
          if (hiddenId != null) {
            setDeletingShiftTruckIds((prev) => {
              const next = new Set(prev);
              next.delete(hiddenId);
              return next;
            });
          }
          const err = e as AxiosError<{ errors?: string[] }>;
          notify(err.response?.data?.errors?.join('、') ?? '勤務の削除に失敗しました', 'error');
          return;
        }
        await refetchAfterDelete();
        notify('勤務を削除しました', 'success');
      },
    });
  }, [deliveries, confirm, getBlockedReason, removeMutateAsync, refetchAfterDelete, notify]);

  return { requestDeleteShift, isDeleting: remove.isPending, deletingShiftTruckIds };
}
